import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import styles from './Avatar.module.scss'

export type AvatarSize = 'sm' | 'md' | 'lg'

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Used to compute the initial and the alt text. */
  name: string
  size?: AvatarSize
  /** Override background color. Defaults to brand ignite. */
  color?: string
  /** Image source; falls back to initial if not provided or fails.
   *  `undefined` is accepted explicitly so callers can pass a nullable
   *  value straight through (exactOptionalPropertyTypes). */
  src?: string | undefined
}

function getInitial(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toUpperCase()
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, size = 'md', color, src, className, style, ...rest },
  ref,
) {
  const initial = getInitial(name)
  const mergedStyle = color ? { ...style, background: color } : style

  return (
    <span
      ref={ref}
      className={cn(styles.avatar, styles[`size-${size}`], className)}
      style={mergedStyle}
      aria-label={name}
      {...rest}
    >
      {src ? <img src={src} alt="" className={styles.image} /> : initial}
    </span>
  )
})
